/**
 * header-overflow-nav.js
 *
 * For each `.header__inline-menu`, keeps the top-level menu on a single
 * row without wrapping and without shrinking the logo. If the items don't
 * all fit in the available width, the ones that don't fit (starting from
 * the end of the menu) are moved into a "More" dropdown at the end of the
 * row. Runs on load and on resize (debounced). Re-checks both directions:
 * items move into "More" when space shrinks, and back out when space grows.
 *
 * Update: hiding "More" is now centralized in one hideMore() helper that
 * also force-closes its <details> element. Previously the trigger's
 * hidden state and its open/closed disclosure state were handled
 * separately, which could leave it visibly open with nothing inside if a
 * user had it open at the moment it got hidden by a recompute.
 */
(function () {
  function initOverflowNav(nav) {
    // Guard against running twice for the same nav (e.g. once on
    // DOMContentLoaded/immediate execution, again on window "load"). Two
    // independent copies of this logic watching the same elements would
    // race each other and could overwrite each other's correct state.
    if (nav.dataset.overflowNavInit === 'true') return;
    nav.dataset.overflowNavInit = 'true';

    var list = nav.querySelector(':scope > ul.list-menu--inline');
    if (!list) return;

    var moreLi = list.querySelector('.header__more-menu-item');
    if (!moreLi) return;

    var moreList = moreLi.querySelector('.header__more-list');
    if (!moreList) return;

    var moreDetails = moreLi.querySelector('details');

    var header = nav.closest('.header');
    var logoEl = header
      ? header.querySelector('.header__heading, .header__heading-link')
      : null;
    var iconsEl = header ? header.querySelector('.header__icons') : null;

    // Original top-level items, excluding the "More" trigger itself.
    var items = Array.prototype.slice
      .call(list.children)
      .filter(function (li) {
        return li !== moreLi;
      });

    function hideMore() {
      moreLi.hidden = true;
      if (moreDetails) moreDetails.open = false;
    }

    function showMore() {
      moreLi.hidden = false;
    }

    function getGap() {
      var styles = window.getComputedStyle(list);
      var gap = parseFloat(styles.columnGap || styles.gap || '0');
      return isNaN(gap) ? 0 : gap;
    }

    function getNavBudget() {
      if (!header) return nav.clientWidth;

      var headerStyles = window.getComputedStyle(header);
      var paddingLeft = parseFloat(headerStyles.paddingLeft) || 0;
      var paddingRight = parseFloat(headerStyles.paddingRight) || 0;
      var contentWidth = header.clientWidth - paddingLeft - paddingRight;

      var logoWidth = logoEl ? logoEl.getBoundingClientRect().width : 0;
      var iconsWidth = iconsEl ? iconsEl.getBoundingClientRect().width : 0;

      var budget = contentWidth - logoWidth - iconsWidth;
      return budget > 0 ? budget : 0;
    }

    function arrange() {
      // Put every item back into the main list, in original order, so we
      // can measure true natural widths before deciding what overflows.
      items.forEach(function (li) {
        if (li.parentNode !== list) {
          list.insertBefore(li, moreLi);
        }
      });
      moreList.innerHTML = '';
      hideMore();
      nav.style.maxWidth = '';

      var available = getNavBudget();
      if (!available) return;

      var gap = getGap();
      var totalWidth = 0;
      items.forEach(function (li, i) {
        totalWidth += li.getBoundingClientRect().width + (i > 0 ? gap : 0);
      });

      // Everything fits on one row  constrain nav to the real budget
      // (so it never visually spills into the logo/icons columns even if
      // the grid would otherwise give it more room) and stop.
      if (totalWidth <= available + 1) {
        hideMore();
        nav.style.maxWidth = available + 'px';
        return;
      }

      // Reveal the trigger so we can measure its real width, then budget
      // the available space around it.
      showMore();
      var moreWidth = moreLi.getBoundingClientRect().width + gap;
      var budget = available - moreWidth;

      var runningWidth = 0;
      var overflowed = [];
      items.forEach(function (li, i) {
        var w = li.getBoundingClientRect().width + (i > 0 ? gap : 0);
        runningWidth += w;
        if (runningWidth > budget) {
          overflowed.push(li);
        }
      });

      if (overflowed.length === 0) {
        hideMore();
      } else {
        overflowed.forEach(function (li) {
          moreList.appendChild(li);
        });
      }

      nav.style.maxWidth = available + 'px';
    }

    var resizeTimer;
    function scheduleArrange() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(arrange, 120);
    }

    window.addEventListener('resize', scheduleArrange);

    // Also watch the header's actual rendered size directly. Webfonts
    // finishing late, the sticky-header script adjusting things, or any
    // other content-driven layout shift can change available space
    // without ever firing a window "resize" event  a ResizeObserver
    // catches those too, so the script can't get stuck on stale state.
    if (window.ResizeObserver && header) {
      var ro = new ResizeObserver(scheduleArrange);
      ro.observe(header);
    }

    arrange();
  }

  function init() {
    document.querySelectorAll('.header__inline-menu').forEach(initOverflowNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Fonts/webfonts loading late can change item widths after DOMContentLoaded.
  window.addEventListener('load', init);
})();